/**
 * Shared submit-then-poll helper for the async job pattern (see
 * design/sbp-generation/async-jobs-plan.md). Long operations (intake analysis,
 * SBP generation) exceed the API Gateway 29s timeout, so the backend returns
 * 202 + job_id immediately and the frontend polls a status endpoint until the
 * job is done or failed — the same transport for every job kind.
 *
 * Locally the backend runs jobs inline, so the submit response already carries
 * a terminal status; `pollJob` resolves on that first response with no GET at
 * all. Deployed, the submit response is `pending` and `pollJob` polls
 * `getStatus$` every `intervalMs` until the job reaches a terminal status.
 */
import { Observable, of, race, throwError, timer } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { JobEnvelope, JobStatusValue } from './job.models';

export interface PollJobOptions {
  /** Delay between status polls, in ms. Defaults to 3000. */
  intervalMs?: number;
  /** Overall wall-clock budget for the job to finish, in ms. Defaults to 4 minutes. */
  timeoutMs?: number;
}

const DEFAULT_INTERVAL_MS = 3000;
const DEFAULT_TIMEOUT_MS = 4 * 60 * 1000;

/** Raised when a job's terminal status is `failed`. */
export class JobFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JobFailedError';
  }
}

/** Raised when a job doesn't reach a terminal status within `timeoutMs`. */
export class JobTimeoutError extends Error {
  constructor(jobId: string, timeoutMs: number) {
    super(`Job ${jobId} did not finish within ${timeoutMs}ms.`);
    this.name = 'JobTimeoutError';
  }
}

/**
 * Raised when a submit response doesn't match the job envelope contract
 * (missing `status`, or non-terminal with no `job_id` to poll on). This
 * usually means the backend endpoint returned its raw result instead of
 * wrapping it as `{ job_id, status, result, error }`.
 */
export class MalformedJobResponseError extends Error {
  constructor(received: unknown) {
    super(
      `Expected a job envelope response (\`{ job_id, status, result, error }\`) but received: ${JSON.stringify(
        received
      )}`
    );
    this.name = 'MalformedJobResponseError';
  }
}

function isTerminal(status: JobStatusValue): boolean {
  return status === 'done' || status === 'failed';
}

/**
 * A job has "settled" once it no longer needs polling right now: it either
 * reached a terminal status, or paused to ask the requester clarifying
 * questions (`awaiting_clarification`).
 */
function isSettled(status: JobStatusValue): boolean {
  return isTerminal(status) || status === 'awaiting_clarification';
}

/** Unwraps a terminal envelope into its result, or throws JobFailedError. */
function resolveResult<T>(envelope: JobEnvelope): T {
  if (envelope.status === 'failed') {
    throw new JobFailedError(envelope.error ?? 'Job failed with no error message.');
  }
  return envelope.result as T;
}

/**
 * Throws JobFailedError for a failed envelope; otherwise returns a plain
 * `JobEnvelope` (status/result/error/project_id only, dropping any other
 * extra fields such as `job_id` on a submit response).
 */
function checkNotFailed(envelope: JobEnvelope): JobEnvelope {
  if (envelope.status === 'failed') {
    throw new JobFailedError(envelope.error ?? 'Job failed with no error message.');
  }
  return {
    status: envelope.status,
    result: envelope.result,
    error: envelope.error,
    project_id: envelope.project_id,
  };
}

function pollUntil(
  jobId: string,
  getStatus$: (jobId: string) => Observable<JobEnvelope>,
  intervalMs: number,
  timeoutMs: number,
  stop: (status: JobStatusValue) => boolean
): Observable<JobEnvelope> {
  const polling$ = timer(0, intervalMs).pipe(
    switchMap(() => getStatus$(jobId)),
    first((envelope) => stop(envelope.status))
  );
  const timeout$ = timer(timeoutMs).pipe(
    switchMap(() => throwError(() => new JobTimeoutError(jobId, timeoutMs)))
  );
  return race(polling$, timeout$);
}

function pollUntilTerminal(
  jobId: string,
  getStatus$: (jobId: string) => Observable<JobEnvelope>,
  intervalMs: number,
  timeoutMs: number
): Observable<JobEnvelope> {
  return pollUntil(jobId, getStatus$, intervalMs, timeoutMs, isTerminal);
}

function pollUntilSettled(
  jobId: string,
  getStatus$: (jobId: string) => Observable<JobEnvelope>,
  intervalMs: number,
  timeoutMs: number
): Observable<JobEnvelope> {
  return pollUntil(jobId, getStatus$, intervalMs, timeoutMs, isSettled);
}

/**
 * Submits a job and polls it to completion.
 *
 * @param submit$ Fires the submitting POST once, on subscription.
 * @param getStatus$ Given a job id, GETs its current status. Called repeatedly
 *   until the job reaches a terminal status.
 * @returns The job's `result`, typed as `T`. Errors if the job fails or times out.
 */
export function pollJob<T>(
  submit$: Observable<JobEnvelope & { job_id: string }>,
  getStatus$: (jobId: string) => Observable<JobEnvelope>,
  options: PollJobOptions = {}
): Observable<T> {
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return submit$.pipe(
    switchMap((submitted) => {
      if (!submitted || !submitted.status) {
        return throwError(() => new MalformedJobResponseError(submitted));
      }
      if (isTerminal(submitted.status)) {
        return of(submitted);
      }
      if (!submitted.job_id) {
        return throwError(() => new MalformedJobResponseError(submitted));
      }
      return pollUntilTerminal(submitted.job_id, getStatus$, intervalMs, timeoutMs);
    }),
    map((envelope) => resolveResult<T>(envelope))
  );
}

/**
 * Polls an already-submitted job to completion, without a submitting POST.
 *
 * Used when a job is triggered server-side as a side effect of another
 * request (e.g. Solution Blueprint generation auto-triggered when intake
 * analysis finishes) — the caller already has a `job_id` and just needs to
 * poll `getStatus$` until it reaches a terminal status.
 *
 * @param jobId The job id to poll.
 * @param getStatus$ Given the job id, GETs its current status. Called
 *   repeatedly until the job reaches a terminal status.
 * @returns The job's `result`, typed as `T`. Errors if the job fails or times out.
 */
export function pollExistingJob<T>(
  jobId: string,
  getStatus$: (jobId: string) => Observable<JobEnvelope>,
  options: PollJobOptions = {}
): Observable<T> {
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return pollUntilTerminal(jobId, getStatus$, intervalMs, timeoutMs).pipe(
    map((envelope) => resolveResult<T>(envelope))
  );
}

/** A job's id paired with its settled (done | failed | awaiting_clarification) envelope. */
export interface SettledJob {
  jobId: string;
  envelope: JobEnvelope;
}

/**
 * Submits a job and polls it until it "settles" — reaches `done`, `failed`,
 * or pauses at `awaiting_clarification`. Unlike `pollJob`, this does NOT
 * unwrap the envelope's `result`: callers need the raw envelope to tell a
 * paused job (clarification questions in `result`) apart from a finished one.
 *
 * Used by flows that can pause for human-in-the-loop clarification, such as
 * document intake analysis.
 *
 * @param submit$ Fires the submitting POST once, on subscription.
 * @param getStatus$ Given a job id, GETs its current status. Called
 *   repeatedly until the job settles.
 * @returns The job id and its settled envelope. Still throws `JobFailedError`
 *   if the job fails, and `JobTimeoutError` if it doesn't settle in time.
 */
export function pollJobUntilSettled(
  submit$: Observable<JobEnvelope & { job_id: string }>,
  getStatus$: (jobId: string) => Observable<JobEnvelope>,
  options: PollJobOptions = {}
): Observable<SettledJob> {
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return submit$.pipe(
    switchMap((submitted) => {
      if (!submitted || !submitted.status) {
        return throwError(() => new MalformedJobResponseError(submitted));
      }
      if (isSettled(submitted.status)) {
        return of({ jobId: submitted.job_id, envelope: checkNotFailed(submitted) });
      }
      if (!submitted.job_id) {
        return throwError(() => new MalformedJobResponseError(submitted));
      }
      const jobId = submitted.job_id;
      return pollUntilSettled(jobId, getStatus$, intervalMs, timeoutMs).pipe(
        map((envelope) => ({ jobId, envelope: checkNotFailed(envelope) }))
      );
    })
  );
}

/**
 * Polls an already-submitted job until it settles, without a submitting
 * POST. Used to resume polling after `POST /intake/analyze/{job_id}/answers`
 * — the caller already has the `job_id` and just needs to poll `getStatus$`
 * until the job settles again (a further clarification round, or `done`).
 *
 * @param jobId The job id to poll.
 * @param getStatus$ Given the job id, GETs its current status. Called
 *   repeatedly until the job settles.
 * @returns The job id and its settled envelope. Still throws `JobFailedError`
 *   if the job fails, and `JobTimeoutError` if it doesn't settle in time.
 */
export function pollExistingJobUntilSettled(
  jobId: string,
  getStatus$: (jobId: string) => Observable<JobEnvelope>,
  options: PollJobOptions = {}
): Observable<SettledJob> {
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return pollUntilSettled(jobId, getStatus$, intervalMs, timeoutMs).pipe(
    map((envelope) => ({ jobId, envelope: checkNotFailed(envelope) }))
  );
}
